import { SIDE_NAV_WIDTH } from "@elyxeer/ui";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { type RefObject, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";

/** px — the bar itself. The notch inset is padding on top of this. */
const HEADER_HEIGHT = 56;

/**
 * px — the distance the reveal is spread over.
 *
 * Roughly one line of the headline, so the bar is arriving while the real title is
 * still leaving. A shorter fade snaps; a much longer one leaves the bar half-drawn
 * through most of the article.
 */
const FADE_DISTANCE = 72;

/**
 * The bar that takes over once the article's own headline scrolls away.
 *
 * The article has no top bar of its own — the way back is an in-page link above the
 * cover. That link is the right control until it leaves the screen, at which point a
 * reader deep in the body has no way back and nothing telling them what they are in.
 * This bar is the answer to both, and it earns its place by only being there once the
 * real headline is not.
 *
 * The reveal is measured from the headline's own position rather than from a scroll
 * offset: the bar fades in exactly as the title slides underneath it. That is one
 * rule instead of a pixel constant per breakpoint, and it stays correct through a
 * resize, a rotation, or the cover image finishing its load and reflowing the page.
 *
 * Opacity is written straight to the node. Scroll fires far faster than React should
 * re-render, and putting the progress in state would re-render the whole article on
 * every frame of every scroll.
 */
export function ArticleCompactHeader({
  title,
  backTo,
  backLabel,
  revealAfterRef,
  standalone = false,
}: {
  title: string;
  backTo: string;
  backLabel: string;
  /** The element whose exit reveals the bar — the article's headline. */
  revealAfterRef: RefObject<HTMLElement | null>;
  /** True for a fullscreen reader that has no desktop side rail. */
  standalone?: boolean;
}) {
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const header = headerRef.current;
    const anchor = revealAfterRef.current;
    if (!header || !anchor) return;

    let frame = 0;

    const paint = () => {
      frame = 0;
      // Viewport-relative, so no scroll offset is involved and nothing has to be
      // recomputed when the layout changes underneath.
      const remaining =
        anchor.getBoundingClientRect().bottom - header.offsetHeight;
      const progress = Math.min(1, Math.max(0, 1 - remaining / FADE_DISTANCE));

      header.style.opacity = String(progress);
      // Transparent has to mean gone, not merely unseen. An invisible bar that still
      // takes keyboard focus and still swallows taps is worse than no bar at all.
      header.style.visibility = progress === 0 ? "hidden" : "visible";
    };

    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(paint);
    };

    paint();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);

    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
    // `title` stands in for "a different article is on screen": the anchor element is
    // reused across a route change, so the ref alone would not re-run this.
  }, [revealAfterRef, title]);

  return (
    <Box
      ref={headerRef}
      data-testid="article.compact-header"
      sx={(theme) => {
        const paper =
          theme.vars?.palette.background.paper ??
          theme.palette.background.paper;

        return {
          position: "fixed",
          top: 0,
          // The side rail is in flow, so the content column — and this bar with it —
          // starts where the rail ends.
          left: standalone ? 0 : { xs: 0, md: `${SIDE_NAV_WIDTH}px` },
          right: 0,
          zIndex: theme.zIndex.appBar,
          // Starts out of the way; the effect above owns both of these from here on.
          // Written inline as well so the first paint cannot flash a solid bar over
          // the headline before the effect has run.
          opacity: 0,
          visibility: "hidden",
          // The same fade the dashboard header uses: opaque behind the control and
          // the title, then falling away, so what scrolls under it dissolves instead
          // of being cut off against a hard edge.
          background: `linear-gradient(to bottom, ${paper} 0%, ${paper} 82%, transparent 100%)`,
          paddingTop: "env(safe-area-inset-top)",
          // Nothing here animates on its own — the position in the article is what
          // sets the opacity — but a transition smooths the jump when a scroll ends
          // between two frames.
          transition: `opacity ${theme.transitions.duration.shortest}ms linear`,
        };
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          height: HEADER_HEIGHT,
          display: "flex",
          alignItems: "center",
          gap: 1,
          paddingInline: { xs: 2.5, md: 5 },
          marginInline: "auto",
        }}
      >
        <IconButton
          component={RouterLink}
          to={backTo}
          aria-label={backLabel}
          data-testid="article.compact-back"
          sx={{
            // Pulled back out of its own padding so the chevron, not the tap target
            // around it, is what lines up with the page gutter.
            marginInlineStart: "-8px",
            color: "text.secondary",
            flexShrink: 0,
          }}
        >
          <ArrowBackIosNewIcon sx={{ fontSize: "16px" }} />
        </IconButton>

        {/*
          One line, clipped with an ellipsis. The headline is the full title; this is
          a reminder of which article the reader is in, and a second line would start
          competing with the prose for the top of the screen.
        */}
        <Typography
          variant="L1"
          noWrap
          data-testid="article.compact-title"
          // Flex children default to `min-width: auto`, which would let the title
          // push the bar wider than the column instead of being clipped inside it.
          sx={{ color: "text.primary", minWidth: 0 }}
        >
          {title}
        </Typography>
      </Container>
    </Box>
  );
}
